import type { Model } from '@ricoh/ria.client.core/model';
import type { Fetcher } from '@ricoh/ria.client.core/plugins/swr/fetcher';
import type { ValueOf } from 'type-fest';

import type { Batch, BatchView, Document, Page } from '~/common/model/api/batch';

import { PROCESS_STAGE_MAP_STATUS } from '~/common/constants/feature/process/batch';

import type { GetDocumentFieldsResponse } from '../_fetcher.type';
import type { GetDocumentFieldsRequestData } from '../_request.type';
import type { TransformDocuments } from './_document.type';

import { prepareDocumentFields } from './_field';

/**
 * Prepare Documents received from service.
 *
 * @param {Batch | BatchView} batch Batch.
 * @param {TransformDocuments} documents Documents.
 * @param {Fetcher<GetDocumentFieldsRequestData, Model<ValueOf<GetDocumentFieldsResponse>>[]> | undefined} getDocumentFieldsFetcher GetDocumentFields fetcher.
 *
 * @returns {Document[]} Documents.
 */
export async function prepareDocuments(
  batch: Batch | BatchView,
  documents: TransformDocuments,
  getDocumentFieldsFetcher?: Fetcher<GetDocumentFieldsRequestData, Model<ValueOf<GetDocumentFieldsResponse>>[]>,
): Promise<Document[]> {
  return Promise.all(
    Object.entries(documents).map(async ([documentId, { pages, fields, stamps, ocr, ...documentInfo }]) => {
      const document: Document = {
        ...documentInfo,
        id: documentId,
        pages: pages
          ? Object.entries(pages).map<Page>(([pageId, page]) => ({
              ...page,
              $is: 'original',
              id: pageId,
              rotation: page.rotation ?? 'noRotation',
            }))
          : [],
        fields: [],
        stamps: Object.entries(stamps ?? {}).map(([stampId, stamp]) => ({
          ...stamp,
          id: stampId,
        })),
      };

      // * Get DocumentFields when batch stage >= validation
      if (getDocumentFieldsFetcher && ![...PROCESS_STAGE_MAP_STATUS.new, ...PROCESS_STAGE_MAP_STATUS.review].includes(batch.status)) {
        // * Get DocumentFields
        const documentFields = await getDocumentFieldsFetcher.handler({
          batchId: batch.id,
          documentId: document.id,
        });

        document.fields = prepareDocumentFields(document, documentFields, fields ?? {}, ocr ?? {});
      }

      return document;
    }),
  );
}
